import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  LinearProgress,
  Alert,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { 
  PlayArrow, 
  Replay, 
  ArrowBack,
  SkipNext,
  Timer
} from '@mui/icons-material';

const tonalColorMap = {
  red: '#d32f2f',
  green: '#2e7d32',
  blue: '#1976d2',
  yellow: '#ed6c02',
  orange: '#ed6c02',
  purple: '#7b1fa2'
};

const YoutubeVideoPlayer = ({ video, onBack, showBackButton = true }) => {
  const hasEndPoint2 = video.endPoint2 !== undefined && video.endPoint2 !== null && video.endPoint2 > 0;
  const hasTonalSections = Array.isArray(video.tonalKeySections) && video.tonalKeySections.length > 0;
  const resolvedPlaybackMode = !hasEndPoint2 && hasTonalSections ? 'tonal' : hasEndPoint2 ? 'phased' : 'single-endpoint';
  const isTonalMode = resolvedPlaybackMode === 'tonal';
  const [player, setPlayer] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('ready'); // ready, playing-phase1, paused-phase1, playing-phase2, playing-tonal, completed
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const playerContainerRef = useRef(null);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const currentPhaseRef = useRef('ready'); // Ref to avoid stale closures in interval
  const needsUnmuteRef = useRef(false);

  // Helper function to update phase in both state and ref
  const updatePhase = (newPhase) => {
    console.log('Phase transition:', currentPhaseRef.current, '→', newPhase);
    setCurrentPhase(newPhase);
    currentPhaseRef.current = newPhase;
  };

  useEffect(() => {
    // Load YouTube API if not already loaded
    if (!window.YT) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(script);
      
      window.onYouTubeIframeAPIReady = () => {
        initializePlayer();
      };
    } else {
      initializePlayer();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.videoId]); // Re-initialize when video changes

  // Cleanup player on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.warn('Error destroying player:', e);
        }
        playerRef.current = null;
      }
    };
  }, []);

  const initializePlayer = () => {
    try {
      if (!playerContainerRef.current) {
        return;
      }

      const newPlayer = new window.YT.Player(playerContainerRef.current, {
        height: '480',
        width: '854',
        videoId: video.videoId,
        playerVars: {
          'autoplay': 0,
          'controls': 1,
          'start': video.startPoint,
          'rel': 0,
          'modestbranding': 1,
          'fs': 1,
          'cc_load_policy': 0,
          'iv_load_policy': 3,
          'enablejsapi': 1,
          'origin': window.location.origin
        },
        events: {
          'onReady': onPlayerReady,
          'onStateChange': onPlayerStateChange,
          'onError': onPlayerError
        }
      });
      setPlayer(newPlayer);
      playerRef.current = newPlayer;
      console.log('YouTube player initialized for video:', video.videoId);
    } catch (error) {
      console.error('Error initializing YouTube player:', error);
    }
  };

  // Define event handlers as regular functions
  const onPlayerReady = (event) => {
    console.log('Player ready for video:', video.videoId);
    setIsPlayerReady(true);
    setCurrentTime(video.startPoint);

    try {
      event.target.seekTo(video.startPoint, true);

      if (isTonalMode) {
        event.target.mute();
        event.target.playVideo();
        startTimeTracking();
        needsUnmuteRef.current = true;
      }
    } catch (error) {
      console.error('Error preparing player on ready:', error);
    }
  };

  const onPlayerStateChange = (event) => {
    console.log('Player state changed:', event.data, 'Current phase:', currentPhase);
    if (event.data === window.YT.PlayerState.PLAYING) {
      console.log('Video started playing, starting time tracking');
      
      // If we're in ready state, transition to phase 1
      if (currentPhaseRef.current === 'ready') {
        updatePhase(resolvedPlaybackMode === 'tonal' ? 'playing-tonal' : 'playing-phase1');
      }
      
      startTimeTracking();
    } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
      if (event.data === window.YT.PlayerState.ENDED && currentPhaseRef.current !== 'completed') {
        updatePhase('completed');
      }
      console.log('Video paused/ended, stopping time tracking');
      stopTimeTracking();
    }
  };

  const onPlayerError = (event) => {
    console.error('YouTube player error:', event.data);
  };

  const startTimeTracking = () => {
    console.log('Starting time tracking for phase:', currentPhaseRef.current);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      const currentPlayer = playerRef.current;
      if (currentPlayer && typeof currentPlayer.getCurrentTime === 'function') {
        try {
          const time = currentPlayer.getCurrentTime();
          
          // Check if we got a valid time
          if (isNaN(time) || time < 0) {
            console.log('Invalid time received:', time);
            return;
          }
          
          const preciseTime = Math.round(time * 10) / 10; // Round to 1 decimal place
          const currentPhaseValue = currentPhaseRef.current;
          
          console.log('Time tracking - Current time:', preciseTime, 'Phase:', currentPhaseValue);
          setCurrentTime(preciseTime);
          
          // Calculate progress and remaining time based on current phase
          if (currentPhaseValue === 'playing-tonal') {
            const currentSection = video.tonalKeySections?.find(
              (section) => time >= section.startTime && time <= section.endTime
            );

            if (currentSection) {
              const sectionDuration = currentSection.endTime - currentSection.startTime;
              const sectionProgress = sectionDuration > 0
                ? ((time - currentSection.startTime) / sectionDuration) * 100
                : 0;
              const remaining = Math.max(0, Math.round((currentSection.endTime - preciseTime) * 10) / 10);
              setProgress(Math.min(Math.max(sectionProgress, 0), 100));
              setRemainingTime(remaining);
            } else {
              setProgress(0);
              setRemainingTime(0);
            }
          } else if (currentPhaseValue === 'playing-phase1') {
            const phaseProgress = ((time - video.startPoint) / (video.endPoint1 - video.startPoint)) * 100;
            setProgress(Math.min(Math.max(phaseProgress, 0), 100));
            
            // Calculate remaining time to end of phase 1
            const remaining = Math.max(0, Math.round((video.endPoint1 - preciseTime) * 10) / 10);
            setRemainingTime(remaining);
            
            console.log('Phase 1 - Progress:', Math.round(phaseProgress), '% Remaining:', remaining, 's');
            
            // Check if we've reached or passed the endpoint with a small buffer
            if (time >= video.endPoint1 - 0.5) {
              console.log('Reached endPoint1 at time:', time);
              currentPlayer.pauseVideo();
              currentPlayer.seekTo(video.endPoint1);
              
              // Check if endPoint2 exists and is greater than 0
              if (video.endPoint2 !== undefined && video.endPoint2 !== null && video.endPoint2 > 0) {
                // Has endPoint2, so pause and offer to continue
                console.log('Has endPoint2, pausing and showing continue button');
                updatePhase('paused-phase1');
                setShowContinueButton(true);
              } else {
                // No endPoint2, mark as completed
                console.log('No endPoint2, completing video');
                updatePhase('completed');
                setShowContinueButton(false);
              }
              
              setRemainingTime(0);
              // Stop tracking by clearing interval directly
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          } else if (currentPhaseValue === 'playing-phase2') {
            const phaseProgress = ((time - video.endPoint1) / (video.endPoint2 - video.endPoint1)) * 100;
            setProgress(Math.min(Math.max(phaseProgress, 0), 100));
            
            // Calculate remaining time to end of phase 2
            const remaining = Math.max(0, Math.round((video.endPoint2 - preciseTime) * 10) / 10);
            setRemainingTime(remaining);
            
            console.log('Phase 2 - Progress:', Math.round(phaseProgress), '% Remaining:', remaining, 's');
            
            // Check if we've reached or passed the endpoint with a tighter buffer
            if (time >= video.endPoint2 - 0.1) {
              console.log('Reached endPoint2 at time:', time, ', completing video');
              currentPlayer.pauseVideo();
              currentPlayer.seekTo(video.endPoint2);
              updatePhase('completed');
              setProgress(100);
              setRemainingTime(0);
              // Stop tracking by clearing interval directly
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        } catch (error) {
          console.error('Error in time tracking:', error);
        }
      } else {
        console.log('Player not ready or getCurrentTime not available');
      }
    }, 250); // Balanced update frequency
  };

  const stopTimeTracking = () => {
    console.log('Stopping time tracking');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handlePlay = () => {
    console.log('handlePlay called - Player ready:', isPlayerReady, 'Player exists:', !!player);
    if (playerRef.current && isPlayerReady) {
      try {
        console.log('Seeking to start point:', video.startPoint);
        playerRef.current.seekTo(video.startPoint);

        console.log('Starting playback after seek');
        playerRef.current.playVideo();
        startTimeTracking();
        updatePhase(resolvedPlaybackMode === 'tonal' ? 'playing-tonal' : 'playing-phase1');
        setShowContinueButton(false);
        setProgress(0);
        setRemainingTime(resolvedPlaybackMode === 'tonal' ? 0 : video.endPoint1 - video.startPoint);
        console.log('Starting playback mode:', resolvedPlaybackMode, 'from', video.startPoint);
      } catch (error) {
        console.error('Error starting playback:', error);
      }
    } else {
      console.error('Cannot start playback - Player not ready or missing');
    }
  };

  const handleContinue = () => {
    if (playerRef.current && currentPhase === 'paused-phase1' && isPlayerReady && video.endPoint2 !== undefined && video.endPoint2 !== null && video.endPoint2 > 0) {
      try {
        playerRef.current.seekTo(video.endPoint1);
        playerRef.current.playVideo();
        startTimeTracking();
        updatePhase('playing-phase2');
        setShowContinueButton(false);
        setProgress(0);
        setRemainingTime(video.endPoint2 - video.endPoint1);
        console.log('Starting Phase 2: ', video.endPoint1, 'to', video.endPoint2);
      } catch (error) {
        console.error('Error continuing playback:', error);
      }
    }
  };

  const handleRestart = () => {
    if (playerRef.current && isPlayerReady) {
      try {
        playerRef.current.seekTo(video.startPoint);
        playerRef.current.pauseVideo();
        updatePhase('ready');
        setShowContinueButton(false);
        setProgress(0);
        setCurrentTime(video.startPoint);
        setRemainingTime(0);
        console.log('Restarting video');
      } catch (error) {
        console.error('Error restarting:', error);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toFixed(1).padStart(4, '0')}`; // Show 1 decimal place
  };

  const formatClockTime = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle keyboard events (placed after function definitions)
  useEffect(() => {
    const unmuteHandler = () => {
      if (!playerRef.current || !needsUnmuteRef.current) {
        return;
      }

      try {
        playerRef.current.unMute();
        needsUnmuteRef.current = false;
      } catch (error) {
        console.error('Error unmuting player:', error);
      }
    };

    document.addEventListener('click', unmuteHandler, true);
    document.addEventListener('keydown', unmuteHandler, true);
    document.addEventListener('touchstart', unmuteHandler, true);

    return () => {
      document.removeEventListener('click', unmuteHandler, true);
      document.removeEventListener('keydown', unmuteHandler, true);
      document.removeEventListener('touchstart', unmuteHandler, true);
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check if spacebar is pressed and we're in paused-phase1 with continue button showing
      if (event.code === 'Space' && showContinueButton && currentPhase === 'paused-phase1') {
        event.preventDefault();
        handleContinue();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyPress);
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [showContinueButton, currentPhase, handleContinue]);
  const activeTonalSection = video?.tonalKeySections?.find(
    (section) => currentTime >= section.startTime && currentTime <= section.endTime
  );
  const activeTonalKey = activeTonalSection?.key || null;
  const activeTonalColorName = activeTonalSection?.color || 'blue';
  const activeTonalColor = tonalColorMap[activeTonalColorName] || tonalColorMap.blue;
  const activeTonalTrackColor = `color-mix(in srgb, ${activeTonalColor} 24%, white)`;
  const shouldUseTonalColor = isTonalMode && Boolean(activeTonalSection) && currentPhase === 'playing-tonal';
  const tonalSectionDuration = activeTonalSection ? activeTonalSection.endTime - activeTonalSection.startTime : 0;
  const tonalSectionProgress = activeTonalSection && tonalSectionDuration > 0
    ? Math.min(Math.max(((currentTime - activeTonalSection.startTime) / tonalSectionDuration) * 100, 0), 100)
    : null;
  const displayedProgress = isTonalMode ? (tonalSectionProgress ?? 0) : progress;
  const showProgressBar = currentPhase === 'playing-phase1' || currentPhase === 'playing-phase2' || currentPhase === 'playing-tonal';

  const getPhaseInfo = () => {
    if (isTonalMode) {
      switch (currentPhase) {
        case 'ready':
          return { text: 'Ready to start tonal playback', color: 'default' };
        case 'playing-tonal':
          return {
            text: activeTonalSection
              ? `${activeTonalSection.key} - ${formatClockTime(remainingTime)} remaining`
              : 'Playing tonal sections',
            color: 'primary'
          };
        case 'completed':
          return { text: 'Tonal playback complete!', color: 'success' };
        default:
          return { text: 'Tonal playback', color: 'default' };
      }
    }

    switch (currentPhase) {
      case 'ready':
        return { text: 'Ready to start', color: 'default' };
      case 'playing-phase1':
        return {
          text: `Playing Phase 1 - ${formatTime(remainingTime)} remaining`,
          color: 'primary'
        };
      case 'paused-phase1':
        return { text: 'Phase 1 Complete - Ready for Phase 2', color: 'warning' };
      case 'playing-phase2':
        return {
          text: `Playing Phase 2 - ${formatTime(remainingTime)} remaining`,
          color: 'secondary'
        };
      case 'completed':
        return { text: 'Video Complete!', color: 'success' };
      default:
        return { text: 'Unknown', color: 'default' };
    }
  };

  const phaseInfo = getPhaseInfo();

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        {showBackButton && (
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{ flexShrink: 0 }}
          >
            Back to Videos
          </Button>
        )}
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
          {video.title || video.name}
        </Typography>
      </Box>

      {/* Main Player Container */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        {/* YouTube Player */}
        <Box 
          sx={{ 
            position: 'relative',
            width: '100%',
            maxWidth: '854px',
            mx: 'auto',
            mb: 3,
            '& iframe': {
              width: '100%',
              height: { xs: '250px', sm: '400px', md: '480px' },
              borderRadius: 2
            }
          }}
        >
          <div id="youtube-player" ref={playerContainerRef}></div>
        </Box>

        {/* Status and Progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Chip 
              label={phaseInfo.text}
              color={phaseInfo.color}
              variant="filled"
              sx={{
                fontWeight: 'bold',
                ...(shouldUseTonalColor ? {
                  backgroundColor: activeTonalColor,
                  color: '#fff'
                } : {})
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Timer sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                Current: {formatTime(currentTime)}
              </Typography>
            </Box>
          </Box>
          
          {showProgressBar && (
            <LinearProgress 
              variant="determinate" 
              value={displayedProgress} 
              sx={{
                height: 8,
                borderRadius: 4,
                ...(shouldUseTonalColor ? {
                  backgroundColor: activeTonalTrackColor,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: activeTonalColor
                  }
                } : {})
              }}
              color={currentPhase === 'playing-phase2' ? 'secondary' : 'primary'}
            />
          )}
          
          {/* Current Tonal Key Display */}
          {isTonalMode && activeTonalKey && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Chip
                label={`🎼 Current Key: ${activeTonalKey}`}
                color={activeTonalColorName === 'red' ? 'error' : activeTonalColorName === 'green' ? 'success' : 'primary'}
                sx={{ 
                  fontSize: '1rem', 
                  py: 2, 
                  px: 1, 
                  fontWeight: 'bold'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Control Buttons */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          {currentPhase === 'ready' && (
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrow />}
              onClick={handlePlay}
              sx={{ minWidth: 150 }}
            >
              {isTonalMode ? 'Start Tonal Playback' : 'Start Playing'}
            </Button>
          )}
          
          {resolvedPlaybackMode === 'phased' && showContinueButton && currentPhase === 'paused-phase1' && hasEndPoint2 && (
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<SkipNext />}
              onClick={handleContinue}
              sx={{ minWidth: 150 }}
            >
              Continue to Phase 2
            </Button>
          )}
          
          {((isTonalMode && currentPhase === 'completed') ||
            (!isTonalMode && (currentPhase === 'paused-phase1' || currentPhase === 'playing-phase2' || currentPhase === 'completed'))) && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<Replay />}
              onClick={handleRestart}
              sx={{ minWidth: 150 }}
            >
              Restart Video
            </Button>
          )}
        </Box>
      </Paper>

      {isTonalMode ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tonal Key Sections
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {video.tonalKeySections.map((section, index) => {
                const sectionColor = tonalColorMap[section.color] || tonalColorMap.blue;
                const sectionTrackColor = `color-mix(in srgb, ${sectionColor} 24%, white)`;
                const isActiveSection = activeTonalSection && activeTonalSection.startTime === section.startTime && activeTonalSection.endTime === section.endTime && activeTonalSection.key === section.key;

                return (
                  <Box
                    key={`${section.key}-${section.startTime}-${section.endTime}-${index}`}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: isActiveSection ? sectionColor : 'grey.300',
                      backgroundColor: isActiveSection ? sectionTrackColor : 'transparent'
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {section.key}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatClockTime(section.startTime)} → {formatClockTime(section.endTime)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Playback Phases
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: currentPhase === 'playing-phase1' ? 'primary.main' : 
                             (currentPhase === 'paused-phase1' || currentPhase === 'playing-phase2' || currentPhase === 'completed') ? 'success.main' : 'grey.300',
                  backgroundColor: currentPhase === 'playing-phase1' ? 'primary.50' : 
                                 (currentPhase === 'paused-phase1' || currentPhase === 'playing-phase2' || currentPhase === 'completed') ? 'success.50' : 'transparent'
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Phase 1: {formatTime(video.startPoint)} → {formatTime(video.endPoint1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {formatTime(video.endPoint1 - video.startPoint)}
                </Typography>
              </Box>
              
              {hasEndPoint2 && (
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: currentPhase === 'playing-phase2' ? 'secondary.main' : 
                               currentPhase === 'completed' ? 'success.main' : 'grey.300',
                    backgroundColor: currentPhase === 'playing-phase2' ? 'secondary.50' : 
                                   currentPhase === 'completed' ? 'success.50' : 'transparent'
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Phase 2: {formatTime(video.endPoint1)} → {formatTime(video.endPoint2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Duration: {formatTime(video.endPoint2 - video.endPoint1)}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Completion Message */}
      {currentPhase === 'completed' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6">
            🎉 Video completed successfully!
          </Typography>
          <Typography>
            You can restart to watch again or return to the video list.
          </Typography>
        </Alert>
      )}

      {/* Debug Information */}
      {process.env.NODE_ENV === 'development' && (
        <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Debug Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <Typography variant="body2">
                <strong>Video ID:</strong> {video.videoId}
              </Typography>
              <Typography variant="body2">
                <strong>Current Time:</strong> {formatTime(currentTime)}
              </Typography>
              <Typography variant="body2">
                <strong>Current Phase:</strong> {currentPhase}
              </Typography>
              <Typography variant="body2">
                <strong>Playback Mode:</strong> {resolvedPlaybackMode}
              </Typography>
              <Typography variant="body2">
                <strong>Player Ready:</strong> {isPlayerReady ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Start Point:</strong> {formatTime(video.startPoint)}
              </Typography>
              {video.endPoint2 !== undefined && video.endPoint2 !== null && video.endPoint2 > 0 && (
                <Typography variant="body2">
                  <strong>End Point 2:</strong> {formatTime(video.endPoint2)}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Video Description */}
      {video.description && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {video.description}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default YoutubeVideoPlayer;